/*
 * Copyright (c) 2024 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
'use client';!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:mta-learning":true});}catch(e){}}();

import * as Dialog from '@radix-ui/react-dialog'
import { useAtomValue, useSetAtom } from 'jotai'
import { Library, TvMinimal } from 'lucide-react'
import Image from 'next/image'
import { useTranslations } from 'next-intl'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { MdClose, MdMenu, MdPhone } from 'react-icons/md'

import { MobileHeaderTopBarTextPopover } from '@/components/e-commerce/header/MobileHeaderTopBarTextPopover'
import { MobileNavigationMenu } from '@/components/e-commerce/header/MobileNavigationMenu'
import { Link } from '@/i18n/navigation'
import { basketTotalQuantityAtom } from '@/store/basket'
import { isMobileMenuAtom } from '@/store/mobile-menu'
import { navigationItemsAtom } from '@/store/navigation-items'
import { isAuthenticatedAtom } from '@/store/session'
import { cn } from '@/utils/css'

/** A description of the available properties for this component. */
type MobileHeaderProps = {
  className?: string
}

/**
 * A mobile version of the header for the e-commerce website, containing the logo for MTa Learning, user/basket
 * interactions, contact information and the primary navigation menu.
 */
const MobileHeader: React.FC<MobileHeaderProps> = ({ className }) => {
  const t = useTranslations('MobileHeader')
  const itemsInBasket = useAtomValue(basketTotalQuantityAtom)
  const navigationItems = useAtomValue(navigationItemsAtom)
  const isAuthenticated = useAtomValue(isAuthenticatedAtom)
  const [isOpen, setIsOpen] = useState(false)
  const setIsMobileMenu = useSetAtom(isMobileMenuAtom)
  const topBarRef = useRef<HTMLDivElement>(null)

  // Sync the mobile menu atom with the dialog open state
  useEffect(() => {
    setIsMobileMenu(isOpen)
  }, [isOpen, setIsMobileMenu])

  /**
   * Handles the change in the open state of the dialog. Ensures that the dialog is closed when the user clicks outside
   * the dialog and the top bar.
   *
   * @param open - whether the dialog is open
   */
  const handleOpenChange = useCallback((open: boolean) => {
    if (!open) {
      // Only close if the click was outside both the modal and the top bar
      const activeElement = document.activeElement as HTMLElement
      if (!topBarRef.current?.contains(activeElement)) {
        setIsOpen(false)
      }
    } else {
      setIsOpen(true)
    }
  }, [])

  return (
    <header className={cn('flex flex-col sticky top-0 z-max', className)}>
      <div
        className={
          'flex justify-center items-center gap-4 p-2 bg-primary-500 text-white text-xs uppercase pointer-events-auto'
        }
        ref={topBarRef}
      >
        <div className={'flex flex-row gap-1 items-center'}>
          <Link href={'/basket'} className={'hover:underline'}>
            {t('basket')}
          </Link>
          {itemsInBasket > 0 && (
            <span className={'flex items-center justify-center rounded-full bg-amber-400 text-black w-5 h-5'}>
              {itemsInBasket}
            </span>
          )}
        </div>
        <MobileHeaderTopBarTextPopover title={t('login')} ariaLabel={t('expandLogin')}>
          <div className={'flex flex-col gap-3 px-1'}>
            <div className={'flex flex-row gap-2 items-center text-white'}>
              <Library className={'h-5 w-5'} />
              <Link href={isAuthenticated ? '/dashboard' : '/login'} className={'font-normal text-sm hover:underline'}>
                {t('kitFacilitatorGuides')}
              </Link>
            </div>
            <div className={'flex flex-row gap-2 items-center text-white'}>
              <TvMinimal className={'h-5 w-5'} />
              <Link href={'https://vela.experientiallearning.org'} className={'font-normal text-sm hover:underline'}>
                {t('onlineActivities')}
              </Link>
            </div>
          </div>
        </MobileHeaderTopBarTextPopover>
        <MobileHeaderTopBarTextPopover title={t('callUs')} ariaLabel={t('expandCallUs')}>
          <div className={'flex flex-col gap-2 px-1'}>
            <div className={'flex flex-row gap-2 items-center text-white'}>
              <MdPhone className={'text-xl'} />
              <a href={'tel:+441937844800'} className={'font-normal text-sm hover:underline'}>
                INT +44 (0)1937 844800
              </a>
            </div>
            <div className={'flex flex-row gap-2 items-center text-white'}>
              <MdPhone className={'text-xl'} />
              <a href={'tel:+12028882776'} className={'font-normal text-sm hover:underline'}>
                US +1 202 888 2776
              </a>
            </div>
          </div>
        </MobileHeaderTopBarTextPopover>
      </div>
      <div className={'flex justify-between items-center bg-white px-4 py-2 border-b border-[#f0f0f0]'}>
        <Link href={'/'}>
          <Image src={'/images/mta-logo.svg'} width={100} height={60} alt={'MTa Learning Logo'} />
        </Link>
        <div className={'flex items-center space-x-4'}>
          <Dialog.Root open={isOpen} onOpenChange={handleOpenChange}>
            <Dialog.Trigger asChild>
              <button
                type={'button'}
                aria-label={t('openMenu')}
                className={'rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-300'}
              >
                <MdMenu aria-hidden={'true'} className={'text-4xl text-primary-500'} />
              </button>
            </Dialog.Trigger>
            <Dialog.Portal>
              <Dialog.Overlay
                className={cn(
                  'bg-black opacity-50 flex flex-col fixed inset-y-0 left-0 h-full w-full z-super-max',
                  'data-[state=open]:animate-in data-[state=open]:slide-in-from-left data-[state=open]:duration-500',
                  'data-[state=closed]:animate-out data-[state=closed]:slide-out-to-left data-[state=closed]:duration-500',
                  'transition ease-in-out motion-reduce:animate-none motion-reduce:transition-none',
                )}
                style={{ top: topBarRef.current?.offsetHeight }}
              />
              <Dialog.Content
                aria-describedby={undefined}
                className={cn(
                  `fixed top-0 left-0 w-[300px] max-w-[90%] bg-white shadow-xl z-super-max`,
                  'transform transition-transform duration-300 ease-in-out motion-reduce:animate-none motion-reduce:transition-none',
                  'data-[state=open]:animate-in data-[state=open]:slide-in-from-left data-[state=open]:duration-500',
                  'data-[state=closed]:animate-out data-[state=closed]:slide-out-to-left data-[state=closed]:duration-500',
                  isOpen ? 'translate-x-0' : 'translate-x-full',
                  'overflow-y-auto',
                )}
                style={{
                  top: topBarRef.current?.offsetHeight,
                  height: `calc(100% - ${topBarRef.current?.offsetHeight}px)`,
                }}
                onPointerDownOutside={(event) => {
                  if (topBarRef.current?.contains(event.target as Node)) {
                    event.preventDefault()
                  }
                }}
              >
                <div className={'flex justify-between items-center p-4 border-b border-[#f0f0f0]'}>
                  <Dialog.Title className={'text-xl font-bold'}>{t('menu')}</Dialog.Title>
                  <Dialog.Close asChild>
                    <button
                      type={'button'}
                      aria-label={t('closeMenu')}
                      className={
                        'rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-300'
                      }
                    >
                      <MdClose aria-hidden={'true'} className={'text-2xl text-primary-500'} />
                    </button>
                  </Dialog.Close>
                </div>
                {navigationItems && <MobileNavigationMenu links={navigationItems} />}
              </Dialog.Content>
            </Dialog.Portal>
          </Dialog.Root>
        </div>
      </div>
    </header>
  )
}

export { MobileHeader }
